// "Pay from Adyvance Wallet" (BILLING-001, D-064): the summary every purchase shows (price, wallet
// balance, after paying) and its pay button. Short of money → the shortfall and a Top up button that
// opens the top-up popup with it filled in; the pay button waits until the balance covers it.
// D-122: `online` adds "Or pay directly" by Fonepay checkout (Fonepay's own page) when the owner
// turned it on: the item is bought once Fonepay confirms, the balance stays the same. (Fonepay
// Dynamic QR, D-085, was dropped on 2026-11-05.)
import { useMutation } from "@tanstack/react-query";
import type { AdyGateway, AdyPayIntent } from "@ady/contracts";
import { formatMoney } from "@ady/format";
import { t } from "@ady/i18n";
import { BrandIcon, Button, Icon, useToast } from "@ady/ui";
import { errorMessage } from "../api/errors";
import { useIdempotencyKeys } from "../api/idempotency";
import { useShell } from "../shell/context";
import { useWallet } from "./api";
import { goToFonepay } from "./payReturn";
import { adyWalletApi, topupFor, useWalletOverview } from "./walletApi";
import { useWalletTopup } from "./WalletTopup";

const money = (p: number) => formatMoney(p);

export function PayFromWallet({
  amountPaisa,
  lines = [],
  paying,
  disabled,
  onPay,
  payLabel,
  online,
}: {
  amountPaisa: number | null;
  /** Extra summary rows above the price (e.g. credits, period). */
  lines?: [string, string][];
  paying: boolean;
  disabled?: boolean;
  onPay: () => void;
  payLabel?: string;
  /** D-084: what a direct online payment buys (null while nothing is chosen). */
  online?: AdyPayIntent | null;
}) {
  const { membership } = useShell();
  const wallet = useWallet(membership.org.id);
  const topup = useWalletTopup();
  const available = wallet.data?.wallet.available_paisa ?? 0;
  const price = amountPaisa ?? 0;
  const short = Math.max(0, price - available);
  const known = Boolean(wallet.data);
  return (
    <div className="flex flex-col gap-2.5" data-testid="pay-from-wallet">
      <dl className="flex flex-col gap-1.5 rounded-sm border border-card bg-surface px-3 py-2.5 text-xs">
        {lines.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between gap-2">
            <dt className="text-fg-secondary">{k}</dt>
            <dd className="font-semibold">{v}</dd>
          </div>
        ))}
        <div className="flex items-center justify-between gap-2">
          <dt className="text-fg-secondary">{t("adyWallet.pay.available")}</dt>
          <dd className="font-semibold tabular-nums">{known ? money(available) : "…"}</dd>
        </div>
        <div className="mt-0.5 flex items-center justify-between border-t border-subtle pt-1.5 text-sm font-bold">
          <dt>{t("adyWallet.pay.price")}</dt>
          <dd className="ady-text-brand tabular-nums">
            {amountPaisa === null ? "—" : money(price)}
          </dd>
        </div>
        {known && amountPaisa !== null && short === 0 ? (
          <div className="flex items-center justify-between gap-2 text-fg-tertiary">
            <dt>{t("adyWallet.pay.after")}</dt>
            <dd className="tabular-nums">{money(available - price)}</dd>
          </div>
        ) : null}
      </dl>
      {known && amountPaisa !== null && short > 0 ? (
        <div className="flex flex-col gap-1.5 rounded-xs border border-warning-border bg-warning-bg px-2.5 py-2">
          <span className="flex items-center gap-1.5 text-2xs font-semibold text-warning-fg">
            <Icon name="wallet" />
            {t("adyWallet.pay.short", { amount: money(short) })}
          </span>
          <Button
            size="md"
            variant="secondary"
            icon="plus"
            onClick={() => topup.open({ amountPaisa: topupFor(short, 0) })}
          >
            {t("adyWallet.pay.topupShort", { amount: money(topupFor(short, 0)) })}
          </Button>
        </div>
      ) : null}
      <Button
        icon="wallet"
        fullWidth
        availability={
          short > 0
            ? { kind: "disabled", reason: t("adyWallet.pay.short", { amount: money(short) }) }
            : disabled || amountPaisa === null || !known
              ? { kind: "disabled", reason: t("adyWallet.pay.title") }
              : undefined
        }
        loading={paying}
        onClick={onPay}
      >
        {payLabel ??
          t("adyWallet.pay.confirm", { amount: amountPaisa === null ? "" : money(price) })}
      </Button>
      {online && amountPaisa !== null && !disabled ? (
        <PayDirect orgId={membership.org.id} intent={online} amountPaisa={amountPaisa} />
      ) : null}
    </div>
  );
}

/** "Or pay Rs X directly" by Fonepay checkout when the owner turned it on (D-122; ads use it, D-086). */
export function PayDirect({
  orgId,
  intent,
  amountPaisa,
}: {
  orgId: string;
  intent: AdyPayIntent;
  amountPaisa: number;
}) {
  const overview = useWalletOverview(orgId);
  const toast = useToast();
  const keys = useIdempotencyKeys();
  const start = useMutation({
    mutationFn: (provider: AdyGateway) => {
      const body = { provider, intent, amount_paisa: amountPaisa };
      return adyWalletApi.startOnline(orgId, body, keys(body));
    },
    onSuccess: (s) => goToFonepay(s),
    onError: (e) => toast.show({ tone: "error", title: errorMessage(e) }),
  });
  const online = overview.data?.topup.online ?? [];
  if (!online.length || amountPaisa <= 0) return null;
  return (
    <div className="flex flex-col gap-1.5 border-t border-subtle pt-2" data-testid="pay-direct">
      <span className="text-xs font-semibold">
        {t("adyWallet.online.direct", { amount: money(amountPaisa) })}
      </span>
      <span className="flex gap-1.5">
        {online.map((provider) => (
          <Button
            key={provider}
            size="sm"
            variant="secondary"
            fullWidth
            loading={start.isPending && start.variables === provider}
            disabled={start.isPending || start.data?.next.kind === "redirect"}
            onClick={() => start.mutate(provider)}
          >
            <BrandIcon brand="fonepay" size={16} />
            {t("adyWallet.online.payWeb")}
          </Button>
        ))}
      </span>
      <span className="text-2xs text-fg-tertiary">{t("adyWallet.online.directHelp")}</span>
    </div>
  );
}
